import React, { useEffect, useState } from "react";
import IndiceUpload from "./indice/IndiceUpload";
import axios from "axios";
import { API_URL, getDailyIndice } from "../../constants";
import { DatePicker, Table, Empty } from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import useTableSearch from "../../hooks/useTableSearch";
import dayjs from "dayjs";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import "./workingmonth.css";

const { RangePicker } = DatePicker;
const dateFormat = "DD.MM.YYYY";

const RANGE_PRESETS = [
  { label: "Azi", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  {
    label: "Ieri",
    value: [
      dayjs().subtract(1, "day").startOf("day"),
      dayjs().subtract(1, "day").endOf("day"),
    ],
  },
  { label: "Ultimele 7 zile", value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Ultimele 30 zile", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Luna curentă", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
  {
    label: "Luna trecută",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
];

const paginationProps = {
  defaultPageSize: 20,
  pageSizeOptions: ["10", "20", "50", "100"],
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (t, r) => `${r[0]}–${r[1]} din ${t} înregistrări`,
};

const Indice = () => {
  const [table, setTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const accessToken = useSelector(
    (state) => state.user.currentUser?.accessToken
  );

  const [minDate, setMinDate] = useState(
    localStorage.getItem("minDateIndice")
      ? new Date(localStorage.getItem("minDateIndice"))
      : new Date(new Date().setHours(0, 0, 0, 0))
  );
  const [maxDate, setMaxDate] = useState(
    localStorage.getItem("maxDateIndice")
      ? new Date(localStorage.getItem("maxDateIndice"))
      : new Date(new Date().setHours(23, 59, 59, 999))
  );
  const getColumnSearchProps = useTableSearch();

  const getTable = async (min = minDate, max = maxDate) => {
    setLoading(true);
    try {
      const res = await axios.post(
        API_URL + "user/getIndice",
        { minDate: min, maxDate: max },
        { headers: { Authorization: "Bearer " + accessToken } }
      );
      if (res.data) {
        setTable(res.data.map((el) => ({ key: el.id, ...el })));
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      const newStartDate = new Date(dates[0]);
      newStartDate.setHours(0, 0, 0, 0);
      const newEndDate = new Date(dates[1]);
      newEndDate.setHours(23, 59, 59, 999);
      localStorage.setItem("minDateIndice", newStartDate);
      localStorage.setItem("maxDateIndice", newEndDate);
      setMinDate(newStartDate);
      setMaxDate(newEndDate);
      getTable(newStartDate, newEndDate);
    }
  };

  const handleDownload = async () => {
    await getDailyIndice(minDate, maxDate);
  };

  const apartamenteUnice = new Set(table.map((el) => el.codId)).size;
  const contoareUnice = new Set(table.map((el) => el.contorId)).size;

  const columns = [
    {
      title: "Cod ID",
      dataIndex: "codId",
      key: "codid",
      sorter: (a, b) => a.codId - b.codId,
      render: (v) => <span className="wm-log-email">{v}</span>,
      ...getColumnSearchProps("codId"),
    },
    {
      title: "Contor ID",
      dataIndex: "contorId",
      key: "contorid",
      sorter: (a, b) => (a.contorId || "").localeCompare(b.contorId || ""),
      ...getColumnSearchProps("contorId"),
    },
    {
      title: "Indice actual",
      dataIndex: "indiceActuali",
      key: "indiceActuali",
      align: "right",
      sorter: (a, b) => a.indiceActuali - b.indiceActuali,
      render: (v) => <span className="wm-log-time">{v}</span>,
      ...getColumnSearchProps("indiceActuali"),
    },
    {
      title: "Prezentat la",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
      render: (v) => (
        <span className="wm-log-time">
          {moment(v).tz("Europe/Chisinau").format("DD.MM.YYYY, HH:mm")}
        </span>
      ),
    },
  ];

  return (
    <div className="wm-page">
      {/* ===== SUMAR ===== */}
      <div className="wm-stats" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <div className="wm-stat wm-stat-good">
          <div className="wm-stat-label">Indici prezentați</div>
          <div className="wm-stat-value">{table.length}</div>
          <div className="wm-stat-meta">în intervalul selectat</div>
        </div>
        <div className="wm-stat">
          <div className="wm-stat-label">Apartamente</div>
          <div className="wm-stat-value">{apartamenteUnice}</div>
          <div className="wm-stat-meta">coduri unice care au prezentat</div>
        </div>
        <div className="wm-stat">
          <div className="wm-stat-label">Contoare</div>
          <div className="wm-stat-value">{contoareUnice}</div>
          <div className="wm-stat-meta">contoare unice cu indici</div>
        </div>
      </div>

      {/* ===== FILTRE + UPLOAD ===== */}
      <div className="wm-grid" style={{ gridTemplateColumns: "3fr 2fr", marginTop: 0 }}>
        <div className="wm-card">
          <h3 className="wm-card-title">
            Indici prezentați de clienți (invoicer.md)
          </h3>
          <div className="wm-filters" style={{ marginBottom: 0 }}>
            <div className="wm-filter">
              <span className="wm-filter-label">Selectați intervalul de date</span>
              <RangePicker
                placeholder={["Data de începere", "Data finală"]}
                defaultValue={[
                  dayjs(moment(minDate).format(dateFormat), dateFormat),
                  dayjs(moment(maxDate).format(dateFormat), dateFormat),
                ]}
                format={dateFormat}
                presets={RANGE_PRESETS}
                onChange={handleDateRangeChange}
                allowClear={false}
              />
            </div>
            <div className="wm-filters-actions">
              <button
                type="button"
                className="wm-btn wm-btn-secondary wm-btn-sm"
                onClick={handleDownload}
              >
                <DownloadOutlined /> Descarcă DBF
              </button>
              <button
                type="button"
                className="wm-btn wm-btn-secondary wm-btn-sm"
                onClick={() => getTable()}
              >
                <ReloadOutlined /> Reîncarcă
              </button>
            </div>
          </div>
          <p className="wm-tab-desc" style={{ marginTop: 10, marginBottom: 0 }}>
            Tabelul se actualizează automat la schimbarea intervalului.
          </p>
        </div>
        <div className="wm-card">
          <h3 className="wm-card-title">Încărcare indici (DBF)</h3>
          <IndiceUpload />
        </div>
      </div>

      {/* ===== TABEL ===== */}
      <div className="wm-card wm-history" style={{ marginTop: 14 }}>
        <Table
          locale={{ emptyText: <Empty description={"Nu există date în intervalul selectat"} /> }}
          scroll={{ x: 700 }}
          columns={columns}
          dataSource={table}
          loading={loading}
          size="small"
          pagination={paginationProps}
        />
      </div>
    </div>
  );
};

export default Indice;
